// Etiquetas de valor para gráficos Recharts.
//
// Regla de densidad: con pocos puntos se etiqueta todo; cuando el eje se llena
// (ej. perfil por hora con 24 barras) se etiqueta solo una muestra (cada N
// puntos + el último) y el tooltip cubre el resto. Así las etiquetas nunca se
// amontonan sin importar la cantidad de datos.
//
// Uso típico:
//   <Bar dataKey="horas">
//     <LabelList content={<EtiquetaValor formatter={fmtNumero} paso={pasoEtiquetas(datos.length)} total={datos.length} />} />
//   </Bar>

const MAX_ETIQUETAS_DEFAULT = 12;

/** Paso de muestreo (1 = etiquetar todos los puntos). */
export function pasoEtiquetas(total: number, max = MAX_ETIQUETAS_DEFAULT): number {
  if (total <= max) return 1;
  return Math.ceil(total / max);
}

interface EtiquetaValorProps {
  formatter: (valor: number) => string;
  /** Paso de muestreo calculado con pasoEtiquetas(). */
  paso?: number;
  /** Total de puntos; permite etiquetar siempre el último aunque el paso lo saltee. */
  total?: number;
  fill?: string;
  fontSize?: number;
  /** Desplazamiento vertical respecto al tope de la marca (negativo = arriba). */
  dy?: number;
  /** Coloca la etiqueta a la derecha de la barra (para BarChart layout="vertical"). */
  horizontal?: boolean;
  // Inyectadas por Recharts vía LabelList content:
  x?: number | string;
  y?: number | string;
  width?: number | string;
  height?: number | string;
  value?: number | string | null;
  index?: number;
}

/**
 * Contenido custom para <LabelList>. Funciona sobre Bar (etiqueta en el tope
 * de la barra) y Line (etiqueta sobre el punto). El texto usa tokens de texto,
 * nunca el color de la serie, para mantener legibilidad en ambos temas.
 */
export function EtiquetaValor({
  formatter,
  paso = 1,
  total,
  fill = 'var(--color-fg-secondary)',
  fontSize = 10,
  dy = -6,
  horizontal = false,
  x,
  y,
  width,
  height,
  value,
  index,
}: EtiquetaValorProps) {
  if (value === null || value === undefined || value === '') return null;
  const idx = index ?? 0;
  const esUltimo = total !== undefined && idx === total - 1;
  if (paso > 1 && idx % paso !== 0 && !esUltimo) return null;

  const nx = Number(x ?? 0);
  const ny = Number(y ?? 0);
  const ancho = width === undefined ? 0 : Number(width);
  const alto = height === undefined ? 0 : Number(height);

  if (horizontal) {
    // Barra horizontal: valor a la derecha del extremo de la barra.
    return (
      <text
        x={nx + ancho + 6}
        y={ny + alto / 2}
        textAnchor="start"
        dominantBaseline="central"
        fontSize={fontSize}
        fill={fill}
      >
        {formatter(Number(value))}
      </text>
    );
  }

  // Barra negativa: el rect cuelga del eje cero, la etiqueta va debajo del extremo.
  const esNegativa = Number(value) < 0 && alto > 0;
  return (
    <text
      x={nx + ancho / 2}
      y={esNegativa ? ny + alto + 12 : ny + dy}
      textAnchor="middle"
      fontSize={fontSize}
      fill={fill}
    >
      {formatter(Number(value))}
    </text>
  );
}

interface EtiquetaDonutProps {
  // number | string porque Recharts tipa así las props del label del Pie.
  cx?: number | string;
  cy?: number | string;
  midAngle?: number | string;
  outerRadius?: number | string;
  percent?: number | string;
  fill?: string;
  fontSize?: number;
  /** Porción mínima (0-1) para dibujar etiqueta; las menores quedan al tooltip. */
  minimo?: number;
}

const RADIANES = Math.PI / 180;

/**
 * Etiqueta de porcentaje fuera del anillo de un donut (<Pie label={...}>).
 * Oculta porciones muy chicas para que no se pisen entre sí.
 */
export function etiquetaDonutPct({
  cx = 0,
  cy = 0,
  midAngle = 0,
  outerRadius = 0,
  percent = 0,
  fill = 'var(--color-fg-secondary)',
  fontSize = 11,
  minimo = 0.04,
}: EtiquetaDonutProps) {
  const pctNum = Number(percent);
  if (pctNum < minimo) return null;
  const cxNum = Number(cx);
  const radio = Number(outerRadius) + 14;
  const px = cxNum + radio * Math.cos(-Number(midAngle) * RADIANES);
  const py = Number(cy) + radio * Math.sin(-Number(midAngle) * RADIANES);
  return (
    <text
      x={px}
      y={py}
      textAnchor={px > cxNum ? 'start' : 'end'}
      dominantBaseline="central"
      fontSize={fontSize}
      fill={fill}
    >
      {`${Math.round(pctNum * 100)}%`}
    </text>
  );
}
